import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/ui';
import { Pagination } from '@/components/ui/Pagination/Pagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { getNextSort } from '@/utils/sorting';
import {
  useAccountsQuery,
  useDashboardEventsQuery,
  useEventSyncMutation,
  usePendingJobsSummaryQuery,
  usePendingJobsToUnlockQuery,
} from '@/services/eventsService';

const syncSchema = z.object({
  eventCode: z.string().trim().min(1, 'Event Id field is required'),
  accountName: z.string().trim().min(1, 'Account field is required'),
});

type SyncFormValues = z.infer<typeof syncSchema>;

type SyncNotice = {
  kind: 'success' | 'error';
  message: string;
};

function getDefaultDateRange() {
  const startDate = new Date();
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + 6);
  return { startDate, endDate };
}

function formatRequestDate(value: Date): string {
  return format(value, 'yyyy-MM-dd');
}

function formatDisplayDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return format(date, 'M/d/yyyy');
}

function getSyncMessage(statusCode: number): SyncNotice {
  if (statusCode === 200) {
    return {
      kind: 'success',
      message: 'The event sync has been successful',
    };
  }
  if (statusCode === 404) {
    return {
      kind: 'error',
      message: 'There is no such combination of event id and account in Certain',
    };
  }

  return {
    kind: 'error',
    message: 'There was an internal server error, please try again',
  };
}

function DashboardModal({
  title,
  children,
  onClose,
}: Readonly<{
  title: string;
  children: ReactNode;
  onClose: () => void;
}>) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <dialog
        open
        className="w-full max-w-3xl border border-cool-gray-2 bg-white p-4 shadow-md"
        aria-label={title}
        onCancel={(event) => {
          event.preventDefault();
          onClose();
        }}
      >
        <div className="mb-4 flex items-center justify-between border-b border-cool-gray-2 pb-2">
          <h2 className="m-0 text-lg font-semibold text-black">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center text-black hover:bg-cool-gray-2 focus:outline-none focus:ring-2 focus:ring-dds-blue"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        {children}
      </dialog>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const accountSelectionInitialized = useRef(false);

  const [dateRange] = useState(getDefaultDateRange);
  const [searchInput, setSearchInput] = useState('');
  const [pager, setPager] = useState({ page: 1, pageSize: 10 });
  const [sort, setSort] = useState({ column: '', direction: 'asc' as 'asc' | 'desc' });
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [isUnlockDialogOpen, setIsUnlockDialogOpen] = useState(false);
  const [syncNotice, setSyncNotice] = useState<SyncNotice | null>(null);
  const [isAccountPanelOpen, setIsAccountPanelOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(searchInput.trim(), 500);

  const accountsQuery = useAccountsQuery();
  const pendingJobsSummaryQuery = usePendingJobsSummaryQuery();
  const pendingJobsToUnlockQuery = usePendingJobsToUnlockQuery(isUnlockDialogOpen);
  const eventSyncMutation = useEventSyncMutation();

  const eventsRequest = useMemo(
    () => ({
      startDate: formatRequestDate(dateRange.startDate),
      endDate: formatRequestDate(dateRange.endDate),
      searchEventText: debouncedSearch,
      accountCode: selectedAccounts.join(','),
      pageNumber: pager.page,
      pageSize: pager.pageSize,
      filters: '',
      sortDirection: sort.column ? sort.direction : '',
      sortColumn: sort.column,
    }),
    [dateRange.endDate, dateRange.startDate, debouncedSearch, pager.page, pager.pageSize, selectedAccounts, sort.column, sort.direction],
  );

  const syncForm = useForm<SyncFormValues>({
    resolver: zodResolver(syncSchema),
    defaultValues: {
      eventCode: '',
      accountName: '',
    },
  });

  useEffect(() => {
    const accountCodes = accountsQuery.data?.map((item) => item.accountCode) ?? [];
    if (accountSelectionInitialized.current || accountCodes.length === 0) {
      return;
    }

    setSelectedAccounts(accountCodes);
    accountSelectionInitialized.current = true;
  }, [accountsQuery.data]);

  useEffect(() => {
    setPager((previousPager) => {
      if (previousPager.page === 1) {
        return previousPager;
      }
      return { ...previousPager, page: 1 };
    });
  }, [debouncedSearch, dateRange.endDate, dateRange.startDate, selectedAccounts]);

  const accountOptions = accountsQuery.data ?? [];
  const isAccountSelectionReady = accountOptions.length === 0
    ? !accountsQuery.isLoading
    : (accountSelectionInitialized.current || selectedAccounts.length > 0);
  const eventsQuery = useDashboardEventsQuery(eventsRequest, isAccountSelectionReady);
  const selectedAccountSet = new Set(selectedAccounts);
  const isAllAccountsSelected =
    accountOptions.length > 0 && selectedAccounts.length === accountOptions.length;
  const pendingTotal = pendingJobsSummaryQuery.data?.totalRemaining ?? 0;
  const isSearchInputTooShort =
    searchInput.trim().length > 0 && searchInput.trim().length < 3;

  const events = eventsQuery.data?.data ?? [];
  const totalRecords = eventsQuery.data?.totalRecords ?? 0;

  const hasBlockingError = Boolean(accountsQuery.error) || Boolean(eventsQuery.error);

  async function onSyncSubmit(values: SyncFormValues) {
    try {
      const response = await eventSyncMutation.mutateAsync(values);
      setSyncNotice(getSyncMessage(response.statusCode));
      await pendingJobsSummaryQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Event sync failed';
      setSyncNotice({ kind: 'error', message: 'There was an internal server error, please try again' });
      toast.error(message);
    }
  }

  function handleAccountToggle(accountCode: string, shouldCheck: boolean) {
    setSelectedAccounts((previous) => {
      if (shouldCheck) {
        if (previous.includes(accountCode)) {
          return previous;
        }
        return [...previous, accountCode];
      }
      return previous.filter((code) => code !== accountCode);
    });
  }

  function handleAllAccountsToggle(shouldCheck: boolean) {
    if (shouldCheck) {
      setSelectedAccounts(accountOptions.map((item) => item.accountCode));
      return;
    }
    setSelectedAccounts([]);
  }

  return (
    <PageContainer title="">
      <section className="space-y-5">
        {/* Click-outside backdrop for account dropdown */}
        {isAccountPanelOpen && (
          <button
            type="button"
            aria-label="Close account selector"
            className="fixed inset-0 z-10 cursor-default bg-transparent"
            onClick={() => setIsAccountPanelOpen(false)}
          />
        )}

        <div className="flex flex-wrap items-end gap-4">
          {/* Compact account selector — chips display with dropdown */}
          <div className="relative w-80 shrink-0 space-y-1">
            <p className="m-0 text-sm font-bold text-black">Select Accounts</p>
            <button
              type="button"
              onClick={() => setIsAccountPanelOpen((v) => !v)}
              className="relative z-20 flex min-h-9 w-full flex-wrap items-center gap-1 border border-cool-gray-2 bg-white px-2 py-1 text-left"
              aria-expanded={isAccountPanelOpen}
              aria-haspopup="listbox"
            >
              {selectedAccounts.length === 0 && (
                <span className="text-sm text-cool-gray-7">No accounts selected</span>
              )}
              {selectedAccounts.map((code) => {
                const account = accountOptions.find((a) => a.accountCode === code);
                return account ? (
                  <span key={code} className="bg-cool-gray-2 px-1.5 py-0.5 text-xs text-black">
                    {account.accountName}
                  </span>
                ) : null;
              })}
            </button>
            {isAccountPanelOpen && (
              <div className="absolute z-20 w-full border border-cool-gray-2 bg-white shadow-md">
                <label className="flex cursor-pointer items-center gap-2 border-b border-cool-gray-2 p-2 text-sm font-semibold text-black hover:bg-cool-gray-1">
                  <input
                    type="checkbox"
                    checked={isAllAccountsSelected}
                    onChange={(event) => handleAllAccountsToggle(event.target.checked)}
                  />
                  {' '}
                  Select All
                </label>
                {accountOptions.map((account) => (
                  <label
                    key={account.accountCode}
                    className="flex cursor-pointer items-center gap-2 p-2 text-sm text-black hover:bg-cool-gray-1"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAccountSet.has(account.accountCode)}
                      onChange={(event) =>
                        handleAccountToggle(account.accountCode, event.target.checked)
                      }
                    />
                    {' '}
                    {account.accountName}
                  </label>
                ))}
                {accountOptions.length === 0 && (
                  <p className="m-0 p-2 text-xs text-cool-gray-11">No accounts available.</p>
                )}
              </div>
            )}
          </div>

          {/* Search + Event Sync — inline, matches Blazor layout */}
          <label className="flex min-w-72 flex-col gap-1 text-sm text-black">
            <span className="sr-only">Search By Event ID/Name</span>
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search By Event ID/Name"
              className="h-9 border border-cool-gray-2 px-2 text-[13px] text-black focus:border-dds-blue focus:outline-none focus:ring-2 focus:ring-dds-blue/30"
            />
          </label>

          <button
            type="button"
            disabled={pendingTotal > 0}
            onClick={() => {
              syncForm.reset({ eventCode: '', accountName: '' });
              setSyncNotice(null);
              setIsSyncDialogOpen(true);
            }}
            className="ml-auto h-9 min-w-28 border border-dds-green bg-dds-green px-3 text-xs font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:border-cool-gray-2 disabled:bg-cool-gray-7"
          >
            {pendingTotal > 0 ? 'In Progress...' : 'Event Sync'}
          </button>
        </div>

        {isSearchInputTooShort && (
          <p className="m-0 text-xs text-dds-red">Enter at least 3 characters to search.</p>
        )}

        <hr className="border-cool-gray-2" />

        <section className="space-y-2">
          <h2 className="m-0 text-2xl font-bold text-black">Certain Events</h2>
          <p className="m-0 text-sm text-black">
            Events in progress of sync:{' '}
            {pendingTotal === 0 ? (
              <span>{pendingTotal}</span>
            ) : (
              <button
                type="button"
                className="border-none bg-transparent p-0 text-sm font-semibold text-dds-blue underline"
                onClick={() => setIsUnlockDialogOpen(true)}
              >
                {pendingTotal}
              </button>
            )}
          </p>
        </section>

        {(eventsQuery.isLoading || accountsQuery.isLoading) && (
          <p className="m-0 text-sm text-cool-gray-11">Loading dashboard data...</p>
        )}

        {hasBlockingError && (
          <p className="m-0 text-sm text-dds-red" role="alert">
            Unable to load dashboard data. Please refresh and try again.
          </p>
        )}

        {!hasBlockingError && (
          <DataTable
            caption="Dashboard events list"
            scrollable={pager.pageSize > 10}
            sortColumn={sort.column}
            sortDirection={sort.direction}
            onSort={(key) => setSort((previous) => getNextSort(previous, key))}
            columns={[
              { key: 'Name', header: 'Event Name', sortable: true },
              { key: 'Code', header: 'Event ID', sortable: true },
              { key: 'StartDate', header: 'Start Date', sortable: true },
              { key: 'EndDate', header: 'End Date', sortable: true },
              { key: 'Status', header: 'Status', sortable: true },
              { key: 'LocationName', header: 'Location', sortable: true },
              { key: 'AccountName', header: 'Account', sortable: true },
              { key: 'SyncStatus', header: 'Synchronization Status', sortable: true },
              { key: 'TotalAttendees', header: 'Total Attendees', sortable: true },
            ]}
          >
                {events.map((eventItem) => (
                  <tr key={`${eventItem.code}-${eventItem.accountCode}`}>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">
                      <button
                        type="button"
                        className="border-none bg-transparent p-0 text-left text-sm font-semibold text-dds-blue hover:underline"
                        onClick={() => navigate(
                          `/event-overview/${encodeURIComponent(eventItem.code)}`,
                          { state: { accountCode: eventItem.accountCode } },
                        )}
                      >
                        {eventItem.name}
                      </button>
                    </td>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">{eventItem.code}</td>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">
                      {formatDisplayDate(eventItem.startDate)}
                    </td>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">
                      {formatDisplayDate(eventItem.endDate)}
                    </td>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">{eventItem.status}</td>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">
                      {eventItem.locationName ?? '-'}
                    </td>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">{eventItem.accountName}</td>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">{eventItem.syncStatus}</td>
                    <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">{eventItem.totalAttendees}</td>
                  </tr>
                ))}

                {events.length === 0 && !eventsQuery.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-4 text-center text-sm text-cool-gray-11">
                      No events available for the selected filters.
                    </td>
                  </tr>
                )}
          </DataTable>
        )}

        <Pagination
          page={pager.page}
          pageSize={pager.pageSize}
          totalRecords={totalRecords}
          onPageChange={(page) => setPager((prev) => ({ ...prev, page }))}
          onPageSizeChange={(pageSize) => setPager({ page: 1, pageSize })}
        />
      </section>

      {isSyncDialogOpen && (
        <DashboardModal title="Event Sync" onClose={() => setIsSyncDialogOpen(false)}>
          <form className="space-y-3" onSubmit={(e) => { void syncForm.handleSubmit(onSyncSubmit)(e); }} noValidate>
            <div className="space-y-1">
              <label htmlFor="event-code" className="text-sm font-semibold text-black">
                Event Id
              </label>
              <input
                id="event-code"
                type="text"
                {...syncForm.register('eventCode')}
                className="h-8 w-full border border-cool-gray-2 px-2 text-sm text-black focus:border-dds-blue focus:outline-none focus:ring-2 focus:ring-dds-blue/30"
              />
              {syncForm.formState.errors.eventCode && (
                <p className="m-0 text-xs text-dds-red" role="alert">
                  {syncForm.formState.errors.eventCode.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label htmlFor="account-name" className="text-sm font-semibold text-black">
                Account
              </label>
              <select
                id="account-name"
                {...syncForm.register('accountName')}
                className="h-11 w-full border border-cool-gray-2 px-2 text-sm text-black focus:border-dds-blue focus:outline-none focus:ring-2 focus:ring-dds-blue/30"
              >
                <option value="">Select Account</option>
                {accountOptions.map((account) => (
                  <option key={account.accountCode} value={account.accountCode}>
                    {account.accountName}
                  </option>
                ))}
              </select>
              {syncForm.formState.errors.accountName && (
                <p className="m-0 text-xs text-dds-red" role="alert">
                  {syncForm.formState.errors.accountName.message}
                </p>
              )}
            </div>

            {syncNotice && (
              <output
                className={syncNotice.kind === 'success' ? 'm-0 text-sm text-dds-green' : 'm-0 text-sm text-dds-red'}
                aria-live="polite"
              >
                {syncNotice.message}
              </output>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsSyncDialogOpen(false)}
                className="h-8 min-w-20 border border-cool-gray-2 bg-white px-3 text-xs font-semibold text-black hover:bg-cool-gray-2"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={eventSyncMutation.isPending}
                className="h-8 min-w-20 border border-dds-green bg-dds-green px-3 text-xs font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:border-cool-gray-2 disabled:bg-cool-gray-7"
              >
                {eventSyncMutation.isPending ? 'Syncing...' : 'Sync'}
              </button>
            </div>
          </form>
        </DashboardModal>
      )}

      {isUnlockDialogOpen && (
        <DashboardModal
          title={`Events in progress of sync: ${pendingTotal}`}
          onClose={() => setIsUnlockDialogOpen(false)}
        >
          <div className="space-y-3">
            <DataTable
              caption="Pending jobs to unlock sync"
              columns={[
                { key: 'eventName', header: 'Event Name' },
                { key: 'eventCode', header: 'Event ID' },
                { key: 'action', header: 'Action' },
              ]}
            >
                  {(pendingJobsToUnlockQuery.data ?? []).map((pendingItem) => (
                    <tr key={`${pendingItem.eventCode}-${pendingItem.accountCode}`}>
                      <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">
                        {pendingItem.eventName}
                      </td>
                      <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">
                        {pendingItem.eventCode}
                      </td>
                      <td className="border-b border-cool-gray-2 px-3 py-2 text-sm text-black">
                        <button
                          type="button"
                          className="border-none bg-transparent p-0 text-sm font-semibold text-dds-blue underline"
                          onClick={() => {
                            setIsUnlockDialogOpen(false);
                            navigate(
                              `/event-overview/${encodeURIComponent(pendingItem.eventCode)}`,
                              {
                                state: {
                                  accountCode: pendingItem.accountCode,
                                  canUnlockSync: true,
                                },
                              },
                            );
                          }}
                        >
                          Click here to unlock sync
                        </button>
                      </td>
                    </tr>
                  ))}

                  {(pendingJobsToUnlockQuery.data ?? []).length === 0 && !pendingJobsToUnlockQuery.isLoading && (
                    <tr>
                      <td colSpan={3} className="px-3 py-4 text-center text-sm text-cool-gray-11">
                        No pending jobs available.
                      </td>
                    </tr>
                  )}
            </DataTable>

            {pendingJobsToUnlockQuery.isLoading && (
              <p className="m-0 text-sm text-cool-gray-11">Loading pending jobs...</p>
            )}

            <p className="m-0 text-sm font-semibold text-dds-red">
              Note: After five minutes of Event Sync, the Unlock Sync will appear.
            </p>
          </div>
        </DashboardModal>
      )}
    </PageContainer>
  );
}
